import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { addDays, endOfMonth, endOfWeek, format, parseISO, subDays } from "date-fns";

import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useBankAccountsWithBalances } from "@/hooks/finance/useBankBalances";
import {
  useRealClosingMatrixRange,
  pickEffectiveClosingAsOf,
  type RealClosingRow,
} from "@/hooks/finance/reconciliation/useRealClosing";
import type {
  ViewPeriod,
  DisplayCurrency,
} from "@/hooks/finance/cash-flow/useCashFlow";

type Args = {
  dateFrom: string;
  dateTo: string;
  viewPeriod: ViewPeriod;
  periodKeys: string[];
  displayCurrency: DisplayCurrency;
};

export type BankRealityPeriodRow = {
  periodKey: string;
  periodStart: string;
  periodEnd: string;
  realOpening: number | null;
  realClosing: number | null;
  realBurn: number | null;
  missingOpening: number;
  missingClosing: number;
  totalAccounts: number;
  byCurrencyOpening: Record<string, number>;
  byCurrencyClosing: Record<string, number>;
};

export type BankRealityResult = {
  byPeriod: Map<string, BankRealityPeriodRow>;
  isLoading: boolean;
  isError: boolean;
};

const PAIR_MAP: Record<string, string> = {
  USD: "USD/ETB",
  EUR: "EUR/ETB",
  GBP: "GBP/ETB",
};

const STABLECOIN_TO_USD: Record<string, number> = {
  USDT: 1,
  USDC: 1,
};

function periodBounds(
  pk: string,
  viewPeriod: ViewPeriod,
  dateFrom: string,
  dateTo: string
): { start: string; end: string } {
  let start: Date;
  let end: Date;

  if (viewPeriod === "daily") {
    start = parseISO(pk);
    end = parseISO(pk);
  } else if (viewPeriod === "weekly") {
    start = parseISO(pk);
    end = endOfWeek(start, { weekStartsOn: 1 });
  } else if (/^\d{4}-Q[1-4]$/.test(pk)) {
    const [yStr, qStr] = pk.split("-Q");
    const year = Number(yStr);
    const q = Number(qStr);
    const startMonth = (q - 1) * 3;
    start = new Date(year, startMonth, 1);
    end = endOfMonth(new Date(year, startMonth + 2, 1));
  } else {
    start = parseISO(`${pk}-01`);
    end = endOfMonth(start);
  }

  if (isNaN(start.getTime()) || isNaN(end.getTime())) {
    return { start: dateFrom, end: dateTo };
  }


  const from = parseISO(dateFrom);
  const to = parseISO(dateTo);
  if (start < from) start = from;
  if (end > to) end = to;

  return { start: format(start, "yyyy-MM-dd"), end: format(end, "yyyy-MM-dd") };
}

export function useBankRealityVsReport(args: Args): BankRealityResult {
  const { dateFrom, dateTo, viewPeriod, periodKeys, displayCurrency } = args;
  const { user } = useAuth();

  const accountsQ = useBankAccountsWithBalances();

  // Extend the fetch window backwards so we can resolve "opening" of the
  // earliest period (which needs the closing as-of the day BEFORE periodStart,
  // possibly from a snapshot recorded weeks earlier).
  const fetchFrom = useMemo(() => {
    if (!dateFrom) return dateFrom;
    return format(subDays(parseISO(dateFrom), 120), "yyyy-MM-dd");
  }, [dateFrom]);

  const bankAccountIds = useMemo(
    () => (accountsQ.data ?? []).map((a) => a.id),
    [accountsQ.data]
  );

  const accountCurrencyById = useMemo(() => {
    const m = new Map<string, string>();
    for (const a of accountsQ.data ?? []) m.set(a.id, a.currency || "ETB");
    return m;
  }, [accountsQ.data]);

  const accountRateTypeById = useMemo(() => {
    const m = new Map<string, string>();
    for (const a of accountsQ.data ?? []) {
      m.set(a.id, (a as any).exchange_rate_type || "bank_rate");
    }
    return m;
  }, [accountsQ.data]);

  const realQ = useRealClosingMatrixRange({
    bankAccountIds,
    dateFrom: fetchFrom,
    dateTo,
  });

  const ratesQ = useQuery({
    queryKey: ["bank-reality-fx-rates", dateTo],
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from("exchange_rates")
        .select("rate, effective_date, rate_type, currency_pair")
        .in("currency_pair", [
          "USD/ETB",
          "EUR/ETB",
          "GBP/ETB",
          "USD/EUR",
          "EUR/USD",
          "USD/GBP",
          "GBP/USD",
          "EUR/GBP",
          "GBP/EUR",
        ])
        .lte("effective_date", dateTo)
        .order("effective_date", { ascending: false })
        .limit(1000);
      if (error) throw error;
      return (data || []) as {
        rate: number;
        effective_date: string;
        rate_type: string;
        currency_pair: string;
      }[];
    },
    enabled: !!user && !!dateTo,
    staleTime: 60_000,
  });

  const convert = useMemo(() => {
    const rates = ratesQ.data || [];
    const rateFor = (date: string, pair: string, rateType = "bank_rate"): number => {
      const found = rates.find(
        (r) =>
          r.currency_pair === pair &&
          r.rate_type === rateType &&
          r.effective_date <= date
      );
      if (found) return Number(found.rate);
      // Last-resort fallback to any rate_type if configured type missing
      const anyType = rates.find(
        (r) => r.currency_pair === pair && r.effective_date <= date
      );
      return anyType ? Number(anyType.rate) : 0;
    };

    return (amount: number, from: string, date: string, rateType = "bank_rate"): number => {
      if (!Number.isFinite(amount) || amount === 0) return 0;
      if (from === displayCurrency) return amount;

      // Stablecoins → USD then route
      if (STABLECOIN_TO_USD[from] != null) {
        const usd = amount * STABLECOIN_TO_USD[from];
        if (displayCurrency === "USD") return usd;
        return convertViaPair(usd, "USD", date, rateType);
      }

      return convertViaPair(amount, from, date, rateType);
    };

    function convertViaPair(amount: number, from: string, date: string, rateType: string): number {
      // Direct pair
      const direct = rateFor(date, `${from}/${displayCurrency}`, rateType);
      if (direct > 0) return amount * direct;
      const inverse = rateFor(date, `${displayCurrency}/${from}`, rateType);
      if (inverse > 0) return amount / inverse;

      // foreign → ETB
      if (displayCurrency === "ETB" && PAIR_MAP[from]) {
        const r = rateFor(date, PAIR_MAP[from], rateType);
        if (r > 0) return amount * r;
      }
      // ETB → foreign
      if (from === "ETB" && PAIR_MAP[displayCurrency]) {
        const r = rateFor(date, PAIR_MAP[displayCurrency], rateType);
        if (r > 0) return amount / r;
      }
      // Cross via ETB
      if (PAIR_MAP[from] && PAIR_MAP[displayCurrency]) {
        const toEtb = rateFor(date, PAIR_MAP[from], rateType);
        const fromEtb = rateFor(date, PAIR_MAP[displayCurrency], rateType);
        if (toEtb > 0 && fromEtb > 0) return (amount * toEtb) / fromEtb;
      }
      return amount;
    }
  }, [ratesQ.data, displayCurrency]);

  const byPeriod = useMemo(() => {
    const out = new Map<string, BankRealityPeriodRow>();
    if (!realQ.data || bankAccountIds.length === 0 || periodKeys.length === 0) {
      return out;
    }

    for (const pk of periodKeys) {
      const { start, end } = periodBounds(pk, viewPeriod, dateFrom, dateTo);
      const openingAsOf = format(subDays(parseISO(start), 1), "yyyy-MM-dd");

      let realOpening: number = 0;
      let realClosing: number = 0;
      let missingOpening = 0;
      let missingClosing = 0;
      const byCurrencyOpening: Record<string, number> = {};
      const byCurrencyClosing: Record<string, number> = {};

      for (const accId of bankAccountIds) {
        const dayMap = realQ.data.byAccount.get(accId);
        const currency = accountCurrencyById.get(accId) || "ETB";
        const rateType = accountRateTypeById.get(accId) || "bank_rate";

        const op = pickEffectiveClosingAsOf(dayMap, openingAsOf);
        const cl = pickEffectiveClosingAsOf(dayMap, end);

        if (op && op.row.real_closing != null) {
          const native = op.row.real_closing;
          byCurrencyOpening[currency] = (byCurrencyOpening[currency] ?? 0) + native;
          realOpening += convert(native, currency, openingAsOf, rateType);
        } else {
          missingOpening += 1;
        }

        if (cl && cl.row.real_closing != null) {
          const native = cl.row.real_closing;
          byCurrencyClosing[currency] = (byCurrencyClosing[currency] ?? 0) + native;
          realClosing += convert(native, currency, end, rateType);
        } else {
          missingClosing += 1;
        }
      }

      out.set(pk, {
        periodKey: pk,
        periodStart: start,
        periodEnd: end,
        realOpening,
        realClosing,
        realBurn: realClosing - realOpening,
        missingOpening,
        missingClosing,
        totalAccounts: bankAccountIds.length,
        byCurrencyOpening,
        byCurrencyClosing,
      });
    }

    return out;
  }, [
    realQ.data,
    bankAccountIds,
    accountCurrencyById,
    accountRateTypeById,
    periodKeys,
    viewPeriod,
    dateFrom,
    dateTo,
    convert,
  ]);

  return {
    byPeriod,
    isLoading: accountsQ.isLoading || realQ.isLoading || ratesQ.isLoading,
    isError: !!realQ.isError || !!ratesQ.isError,
  };
}
